import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Quote, MessageCircleHeart } from "lucide-react";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/testimonials`);
        // setTestimonials(res.data);
        setTestimonials(res.data.filter((t) => t.isVerified));
      } catch (err) {
        console.error("Failed to load testimonials:", err);
      }
    };
    fetchTestimonials();
  }, []);

  // Limit to 9 if showAll is false
  const displayedTestimonials = showAll
    ? testimonials
    : testimonials.slice(0, 9);

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product", // Or "Organization" if reviews are general
    name: "Pak Fashions",
    description: "Handcrafted fashion loved by our customers.",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue:
        testimonials.length > 0
          ? (
              testimonials.reduce((sum, t) => sum + (t.rating || 5), 0) /
              testimonials.length
            ).toFixed(1)
          : "5",
      reviewCount: testimonials.length,
    },
    review: testimonials.slice(0, 5).map((t) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: t.name,
      },
      datePublished: new Date(t.date).toISOString(),
      reviewBody: t.message,
      reviewRating: {
        "@type": "Rating",
        ratingValue: t.rating || 5,
        bestRating: 5,
        worstRating: 1,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-gray-50 py-24 px-6 sm:px-12 lg:px-24 font-sans text-black mt-16 relative overflow-hidden">
      {/* Background glow logic */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-96 bg-yellow-200 rounded-full blur-[120px] opacity-20 pointer-events-none"></div>
      
      {/* SEO Helmet */}
      <Helmet>
        <title>Customer Testimonials | Pak Fashions</title>
        <meta
          name="description"
          content="Read verified reviews and heartfelt testimonials from Pak Fashions customers. Real experiences that highlight our quality and service."
        />
        <meta
          name="keywords"
          content="Pak Fashions testimonials, customer reviews, fashion feedback, verified testimonials, user experience"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph (Facebook, LinkedIn) */}
        <meta
          property="og:title"
          content="Customer Testimonials | Pak Fashions"
        />
        <meta
          property="og:description"
          content="Discover what our happy customers are saying about their Pak Fashions experience."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://pakfashions.co.ke/testimonials"
        />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/pak-fashions.jpg"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Customer Testimonials | Pak Fashions"
        />
        <meta
          name="twitter:description"
          content="Real stories from real customers. See what people are saying about Pak Fashions."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/pak-fashions.jpg"
        />
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="text-center max-w-3xl mx-auto mb-20 relative z-10">
        <MessageCircleHeart className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
        <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight">
          Love from our <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Community</span>
        </h1>
        <p className="text-xl text-gray-500 font-medium leading-relaxed">
          Real experiences from style icons who love our boutique collections just as much as we do.
        </p>
        <div className="w-24 h-1.5 bg-yellow-400 rounded-full mx-auto mt-8 shadow-sm"></div>
      </div>

      {testimonials.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-[3rem] border border-gray-100 shadow-sm max-w-4xl mx-auto relative z-10">
          <p className="text-gray-400 font-medium text-lg">We're gathering stories from our lovely customers. Check back soon!</p>
        </div>
      ) : (
        <div className="relative z-10">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
            {displayedTestimonials.map((t) => (
              <div
                key={t.id}
                className="bg-white border border-gray-100/50 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl hover:border-yellow-200 transition-all duration-300 relative group hover:-translate-y-1 flex flex-col h-full"
              >
                <div className="absolute top-8 right-8 w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform group-hover:rotate-12 transition-all duration-500">
                  <Quote size={20} className="text-yellow-500" />
                </div>
                <Quote
                  className="text-yellow-300 mb-6 group-hover:text-yellow-400 transition-colors duration-300"
                  size={40}
                />

                <p className="text-gray-600 text-lg leading-relaxed mb-10 italic flex-grow">
                  "{t.message}"
                </p>

                <div className="pt-6 border-t border-gray-50 flex items-center justify-between mt-auto">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 leading-none mb-2">{t.name}</h4>
                    <p className="text-sm font-medium text-gray-400">
                      {new Date(t.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  {t.isVerified && (
                    <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full border border-green-200">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      <span className="text-xs font-bold uppercase tracking-wider">
                        Verified
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Show More / Show Less Button */}
          {testimonials.length > 9 && (
            <div className="text-center mt-16 pb-12">
              <button
                onClick={() => setShowAll(!showAll)}
                className="bg-gray-900 hover:bg-black text-white font-bold py-4 px-10 rounded-full shadow-lg shadow-gray-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                {showAll ? "Read Fewer Stories" : "Read More Stories"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Testimonials;
